<template>
  <div class="root">
    <div class="content">
      <div class="section mb-5 print-hide">
        <div class="legend">
          <div
            v-for="demographic in genderDemographics"
            :key="demographic.key + '_gender_legend'"
            class="item"
          >
            <span class="square" />
            {{ Math.round((100 * demographic.value) / genderTotal) }}%
            {{ demographic.key }}
          </div>
        </div>
        <div class="bar">
          <div
            v-for="demographic in genderDemographics"
            :key="demographic.key"
            tabindex="0"
            role="img"
            class="area"
            :style="{ width: (100 * demographic.value) / genderTotal + '%' }"
            :aria-label="
              Math.round((100 * demographic.value) / genderTotal) +
                '% ' +
                demographic.key
            "
          />
        </div>
      </div>

      <strong class="print-only-block">Gender</strong>
      <ul class="print-data-list cells print-only-inline">
        <li
          v-for="demographic in genderDemographics"
          :key="demographic.key + '_ethnicity_legend'"
        >
          {{ demographic.key }}
          <strong>
            {{ Math.round((100 * demographic.value) / genderTotal) }}%
          </strong>
          <span class="bar-container">
            <span
              class="bar"
              :style="{ width: (100 * demographic.value) / genderTotal + '%' }"
            ></span>
          </span>
        </li>
      </ul>

      <div class="section mb-3 print-hide">
        <div class="legend">
          <div
            v-for="demographic in ethnicityDemographics"
            :key="demographic.key + '_ethnicity_legend'"
            class="item"
          >
            <span class="square" />
            {{ Math.round((100 * demographic.value) / ethnicityTotal) }}%
            {{ demographic.key }}
          </div>
        </div>
        <div class="bar">
          <div
            v-for="demographic in ethnicityDemographics"
            :key="demographic.key"
            class="area"
            tabindex="0"
            role="img"
            :style="{ width: (100 * demographic.value) / ethnicityTotal + '%' }"
            :aria-label="
              Math.round((100 * demographic.value) / ethnicityTotal) +
                '% ' +
                demographic.key
            "
          />
        </div>
      </div>

      <strong class="print-only-block">Ethnicity</strong>
      <ul class="print-data-list cells print-only-inline">
        <li
          v-for="demographic in ethnicityDemographics"
          :key="demographic.key + '_ethnicity_legend'"
        >
          {{ demographic.key }}
          <strong>
            {{ Math.round((100 * demographic.value) / genderTotal) }}%
          </strong>
          <span class="bar-container">
            <span
              class="bar"
              :style="{ width: (100 * demographic.value) / genderTotal + '%' }"
            ></span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { sumBy } from '@/helpers';

const genderOptions = {
  male: 'Male',
  female: 'Female',
  transgender_female: 'Transgender-Female',
  transgender_male: 'Transgender-Male',
  non_binary: 'Non-Binary',
  prefer_not_to_say: 'Preferred not to answer',
  unknown: 'Exploring/Unknown',
  other: 'Not Listed / Self-Identify / Other',
  none: 'None',
  'n/a': 'N/A'
};

const ethnicityOptions = {
  white: 'White',
  black: 'Black or African American',
  hispanic: 'Hispanic',
  pacific: 'Pacific',
  asian: 'Asian',
  native: 'American Indian or Alaskan Native',
  multiracial: 'Multiracial',
  prefer_not_to_answer: 'Preferred not to answer',
  other: 'Other'
};

export default {
  name: 'Demographics',

  props: {
    loading: {
      type: Boolean,
      default: () => false
    },
    demographicData: {
      type: Object,
      default: () => {}
    }
  },

  computed: {
    demographics() {
      return Object.entries(this.demographicData)
        .map(([key, value]) => ({ key, value }))
        .filter((x) => x.value > 0);
    },

    genderDemographics() {
      return this.demographics
        .filter((x) => x.key.startsWith('gender_'))
        .map(({ key, value }) => ({ key: key.replace('gender_', ''), value }))
        .map(({ key, value }) => ({ key: genderOptions[key], value }));
    },

    genderTotal() {
      return this.genderDemographics.reduce(sumBy('value'), 0);
    },

    ethnicityDemographics() {
      return this.demographics
        .filter((x) => x.key.startsWith('race_'))
        .map(({ key, value }) => ({ key: key.replace('race_', ''), value }))
        .map(({ key, value }) => ({ key: ethnicityOptions[key], value }));
    },

    ethnicityTotal() {
      return this.ethnicityDemographics.reduce(sumBy('value'), 0);
    }
  }
};
</script>

<style lang="scss" scoped>
.root {
  width: 100%;
  padding: 0 2.45rem;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 8px;
  color: #76788E;
  font-size: 15px;
  font-weight: 500;
  line-height: 1;

  .item {
    display: inline-flex;
    margin-right: 20px;
    padding-bottom: 4px;

    .square {
      display: inline-block;
      height: 16px;
      width: 16px;
      background: $dark-blue;
      border-radius: 1px;
      margin-right: 4px;
    }

    @include use-color-list('.square');
  }
}

.bar {
  display: flex;
  width: 100%;
  border-radius: 2px;
  height: 24px;
  background: darken($dark-blue, 33%);

  .area {
    height: 24px;
    min-width: 8px;
    background: $dark-blue;
    position: relative;
    z-index: 2;
    transition: all 0.2s ease;
    border-bottom: 0;

    @include use-color-list();

    &:first-child {
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
    }

    &:last-child {
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
    }

    @media screen and (min-width: $md-min) {
      &::before {
      position: absolute;
      top: 1px;
      left: 5%;
      background-color: $white;
      border-radius: $border-radius;
      color: #fff;
      content: attr(aria-label);
      padding: 4px 8px;
      transition: all 0.5s ease;
      white-space: nowrap;
      font-size: 0.9rem;

      @include box-shadow(0, 1px, 4px, rgba($dark-blue, 0.1));
    }

    &::after {
      position: absolute;
      top: -1px;
      left: calc(5% + 3px);
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid #fff;
      content: ' ';
      font-size: 0;
      line-height: 0;
      width: 0;
    }

    &::before,
    &::after {
      opacity: 0;
      pointer-events: none;
    }

    &:focus,
    &:hover {
      border-bottom: 2px solid $dark-blue;
    }

    &:focus::before,
    &:hover::before {
      top: -25px;
      color: $dark-blue;
    }

    &:focus::before,
    &:focus::after,
    &:hover::before,
    &:hover::after {
      opacity: 1;
      transition: all 0.3s ease;
    }
    }
  }
}

@media print {
  .dashboard-card .content {
    &.my-4 {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }

    &.px-3 {
      padding-right: 0 !important;
      padding-left: 0 !important;
    }

    .section.mb-5 {
      margin-bottom: 0 !important;
    }
  }
}
</style>
